import React, { useRef, useState } from "react";
import "./ResultPage.css"; // We'll create this new CSS file below
import Alert from "./Alert";

function ResultPage({ result, onRestartQuiz, onNavigateToShare, setQuizResult }) {
  const [formData, setFormData] = useState({
    firstName: "",
    emailAddress: "",
    phoneNumber: "",
    agreesToUpdates: true,
  });
  const alertRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setQuizResult((prev) => ({
      ...prev,
      firstName: formData.firstName,
    }));
    onNavigateToShare();
  };

  const quizURL = window.location.href; // Get the base URL of the quiz
  const shareMessage = `I just took this quiz and matched with ${result.medicationName}! Find out your match in 60 seconds!`;

  const encodeURL = (url) => encodeURIComponent(url);
  const encodeText = (text) => encodeURIComponent(text);

  const shareOnFacebook = () => {
    const urlToShare = `https://injectco.com/`;
    const messageForQuote = shareMessage;
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?src=sp&u=${encodeURL(urlToShare)}&quote=${encodeText(messageForQuote)}`;

    window.open(facebookShareUrl, "_blank", "width=600,height=400");
  };

  const shareOnTwitter = () => {
    const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeText(shareMessage)}&url=${encodeURL(quizURL + "?utm_source=twitter&utm_medium=quiz_share")}`;
    window.open(twitterShareUrl, "_blank", "width=600,height=400");
  };

  const shareOnWhatsapp = () => {
    const whatsappShareUrl = `https://api.whatsapp.com/send?text=${encodeText(shareMessage)}%20${encodeURL(quizURL + "?utm_source=whatsapp&utm_medium=quiz_share")}`;

    window.open(whatsappShareUrl, "_blank");
  };

  const copyLink = async () => {
    try {
      const textToCopy = window.location.href;
      await navigator.clipboard.writeText(textToCopy);
      if (alertRef.current) {
        alertRef.current.showAlert("Link copied!");
      }
    } catch (err) {
      console.error("Failed to copy link: ", err);
      alert("Failed to copy link. Please try again or copy manually.");
    }
  };

  return (
    <>
      <div className="logo-container">
        <img src="/assets/images/logo-white.png" alt="InjectCo Logo" className="logo-image" />
      </div>
      <div className="injectco-results-container">
        <div className="progress-indicator-bar">
          <div className="progress-bar-fill" style={{ width: "95%" }}></div>
          <span>95% Complete...</span>
        </div>

        <main className="results-page-content">
          <div className="results-status-header">
            <img src="/assets/images/checkbox-green.png" alt="Ready" className="status-checkmark" />
            <h2>Your Results Are Ready</h2>
          </div>
          <p className="results-page-intro">We've reviewed your answers and prepared a clinically tailored weight loss plan—backed by real science and delivered by real medical providers.</p>

          <section className="plan-details-section">
            <p>Your custom plan includes:</p>
            <ul>
              <li>
                Your recommended <strong>GLP-1 medication</strong> (Semaglutide, Tirzepatide, or Oral GLP-1)
              </li>
              <li>
                A <strong>doctor-approved treatment timeline</strong>
              </li>
              <li>
                <strong>Pricing, dosage, and how soon your medication ships</strong> (most orders go out in under 24 hours)
              </li>
            </ul>
          </section>

          <form id="personalizedPlanForm" className="personalized-plan-cta-form" onSubmit={handleSubmit}>
            <h3>
              <span role="img" aria-label="email icon">
                <img src="/assets/images/letter.png" alt="Email Icon" className="email-icon" />
              </span>
              Where should we send your personalized plan?
            </h3>
            <div className="form-container">
              <div className="form-field-group">
                <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} required />
              </div>
              <div className="form-field-group">
                <input type="email" name="emailAddress" placeholder="Email Address" value={formData.emailAddress} onChange={handleChange} required />
              </div>
              <div className="form-field-group">
                <input type="tel" name="phoneNumber" placeholder="Phone Number (optional)" value={formData.phoneNumber} onChange={handleChange} />
              </div>
            </div>
            <span className="checkbox-details-text">Get SMS reminders, refill updates, and exclusive offers.</span>

            <div className="form-field-group checkbox-container">
              <input type="checkbox" id="agreesToUpdates" name="agreesToUpdates" checked={formData.agreesToUpdates} onChange={handleChange} />
              <label htmlFor="agreesToUpdates">Yes, send me updates and special offers from InjectCo</label>
            </div>

            <section className="trust-injectco-section">
              <h4>Why Trust InjectCo?</h4>
              <ul>
                <li>
                  <img src="/assets/images/check-mark.svg" alt="Check" /> FDA-Approved Medications
                </li>
                <li>
                  <img src="/assets/images/check-mark.svg" alt="Check" /> U.S. Licensed Medical Providers
                </li>
                <li>
                  <img src="/assets/images/check-mark.svg" alt="Check" /> HIPAA-Compliant & Private
                </li>
                <li>
                  <img src="/assets/images/check-mark.svg" alt="Check" /> 24-Hour Shipping Available
                </li>
              </ul>
            </section>

            <section className="testimonial-block">
              <img src="/assets/images/results-page-img.webp" alt="Testimonial from Rachel" className="testimonial-image-display" />
              <div className="testimonial-quote-content">
                <p>"I lost 22 lbs in just 3 months—without the crash diets or cravings. InjectCo made it so easy."</p>
                <span>– Rachel, 38</span>
              </div>
            </section>
            <section className="share-section">
              <h3>
                <img style={{ width: 20, verticalAlign: "middle", marginRight: 5 }} src="/assets/images/checkbox-green.png" alt="Checkmark" />
                Share your match with friends
              </h3>
              <p style={{ fontStyle: "italic" }}>I just took InjectCo's weight loss quiz and matched with {result.medicationName}! Find out your match in 60 seconds!</p>

              <div className="social-sharing">
                <button onClick={shareOnFacebook} className="share-button facebook" type="button">
                  Share on Facebook
                </button>
                <button onClick={shareOnTwitter} className="share-button twitter" type="button">
                  Tweet Your Results
                </button>
                <button onClick={shareOnWhatsapp} className="share-button whatsapp" type="button">
                  Share on WhatsApp
                </button>
                <button onClick={(e) => {  copyLink() }} className="share-button link" type="button">
                  Copy Link
                </button>
              </div>
            </section>
            <section className="final-cta-section">
              <p className="urgency-message-text">
                <span role="img" aria-label="alarm clock">
                  ⏰
                </span>
                Hurry! Your personalized plan is being held for the next <strong>10 minutes</strong>.
              </p>
              <p className="lockin-pricing-text">Lock in today's availability and pricing now.</p>
              <button type="submit" form="personalizedPlanForm" className="unlock-plan-main-button">
                UNLOCK MY PERSONALIZED PLAN →
              </button>
              <p className="no-payment-required-text">Get instant access • No payment required</p>
            </section>
          </form>
        </main>
      </div>
      <Alert ref={alertRef} />
    </>
  );
}

export default ResultPage;
